import type {
	AcceptanceCheck, AutoModeStatus, DraftFile, DraftSummary, ExpandedKey, FigmaBridgeToken, Firing, LoopDetail, LoopListItem, TraceChunk, McpCatalogEntry, McpList, MemoryEntry, MemoryProposal, MemoryTree, McpAccount, NoteCollection, NoteEntry, NoteIndexEntry, McpConnection, ModelSpec, ProfileList, ProfileModelState, ProjectDetail, ProjectSummary, ProviderModels, Repeat, ScheduleDetail, ScheduleList, ScheduleSummary, SessionSummary, SidebarCategory, SidebarView, SkillDetail, Snapshot, TaskPriority, TaskRecord, TaskStatus, UsageReport, User, TaskKind, TrackedStatus, TicketRequest, TicketSpec, DeploymentBatch, AcceptanceRevisionHistory } from "./types";
import {
	researchApiBase,
	type AnalysisSummary,
	type EvidenceImpacts,
	type EvidenceSummary,
	type QualitySummary,
	type RecordRead,
	type ResearchCapabilities,
	type ResearchMapView,
	type ResearchRecord,
	type ResearchScopeRef,
	type ReviewedRevisions,
	type StoredRecord,
} from "../../shared/wire/research.ts";

export class ApiError extends Error {
	constructor(
		readonly status: number,
		message: string,
		/** Seconds to wait, sent with a 429 so the caller can count down instead of guessing. */
		readonly retryAfter?: number,
		/** The whole error body, for the answers that carry more than a message. */
		readonly body?: unknown,
	) {
		super(message);
	}

	/** Status 0 means the request never reached the server. */
	get isOffline(): boolean {
		return this.status === 0;
	}

	get isThrottled(): boolean {
		return this.status === 429;
	}
}

/**
 * A save refused because the document changed after the editor loaded it: what is there now
 * (null once it is gone). Undefined for any other failure. 412, or the status the editor's
 * contract names instead (409 for a profile's default model).
 */
export function staleCopy<T>(err: unknown, status: 409 | 412 = 412): { current: T | null } | undefined {
	if (!(err instanceof ApiError) || err.status !== status) return undefined;
	return { current: ((err.body as { current?: T } | null)?.current ?? null) as T | null };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	let res: Response;
	try {
		res = await fetch(path, {
			...init,
			headers: init?.body instanceof FormData ? init?.headers : { "content-type": "application/json", ...init?.headers },
		});
	} catch {
		// fetch only rejects when the request never reached the server. Status 0 marks that
		// apart from a real answer, so a dead backend does not read as "wrong password".
		throw new ApiError(0, "无法连接服务端，请稍后重试");
	}
	const text = await res.text();
	const data = text ? (JSON.parse(text) as unknown) : null;
	if (!res.ok) {
		const body = data as { error?: string; retryAfter?: number } | null;
		throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`, body?.retryAfter, data);
	}
	return data as T;
}

export const api = {
	me: () => request<{ user: User }>("/api/auth/me"),
	login: (userId: string, password: string) =>
		request<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify({ userId, password }) }),
	logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

	profiles: () => request<ProfileList>("/api/profiles"),
	reloadProfiles: () => request<{ loaded: string[]; broken: string[] }>("/api/profiles/reload", { method: "POST" }),
	drafts: () => request<{ builder: string; drafts: DraftSummary[] }>("/api/drafts"),
	draftFiles: (name: string) => request<{ files: DraftFile[] }>(`/api/drafts/${name}/files`),
	enableDraft: (name: string, owner: "me" | "shared" | "keep") =>
		request<{ enabled: string; owner: string; diagnostics: DraftSummary["diagnostics"] }>(`/api/drafts/${name}/enable`, { method: "POST", body: JSON.stringify({ owner }) }),

	sessions: () => request<{ sessions: SessionSummary[] }>("/api/sessions"),
	createSession: (profile: string, opts: { projectId?: string; anonymous?: boolean } = {}) =>
		request<{ session: SessionSummary }>("/api/sessions", { method: "POST", body: JSON.stringify({ profile, projectId: opts.projectId, anonymous: opts.anonymous }) }),
	session: (id: string) => request<{ session: SessionSummary; snapshot: Snapshot }>(`/api/sessions/${id}`),
	archiveSession: (id: string) => request<{ ok: true }>(`/api/sessions/${id}`, { method: "DELETE" }),
	renameSession: (id: string, title: string) => request<{ title: string }>(`/api/sessions/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
	fileSession: (id: string, projectId: string) => request<{ projectId: string }>(`/api/sessions/${id}/project`, { method: "POST", body: JSON.stringify({ projectId }) }),
	branchSession: (id: string, entryId: string) =>
		request<{ session: SessionSummary; draft?: string }>(`/api/sessions/${id}/branch`, { method: "POST", body: JSON.stringify({ entryId }) }),
	send: (id: string, text: string) =>
		request<{ accepted: true }>(`/api/sessions/${id}/messages`, { method: "POST", body: JSON.stringify({ text }) }),
	abort: (id: string) => request<{ ok: true }>(`/api/sessions/${id}/abort`, { method: "POST" }),
	setSharing: (id: string, users: string[]) =>
		request<{ session: SessionSummary }>(`/api/sessions/${id}/sharing`, { method: "PUT", body: JSON.stringify({ users }) }),
	releaseLock: (id: string) => request<{ ok: true }>(`/api/sessions/${id}/lock`, { method: "DELETE" }),
	models: () => request<{ providers: ProviderModels[] }>("/api/models"),
	usage: (days: number) => request<UsageReport>(`/api/usage?days=${days}`),
	setModel: (id: string, model: ModelSpec | null) =>
		request<{ model: { provider: string; id: string } | null; thinkingLevel?: string }>(`/api/sessions/${id}/model`, { method: "PUT", body: JSON.stringify(model) }),
	users: () => request<{ users: User[] }>("/api/users"),
	commandOptions: (id: string, name: string) =>
		request<{ options: Record<string, string[]> }>(`/api/sessions/${id}/commands/${encodeURIComponent(name)}/options`),
	runCommand: (id: string, name: string, values: Record<string, string | boolean>) =>
		request<{ output: string }>(`/api/sessions/${id}/commands/${encodeURIComponent(name)}/run`, { method: "POST", body: JSON.stringify({ values }) }),
	upload: (id: string, files: File[]) => {
		const form = new FormData();
		for (const f of files) form.append("file", f);
		return request<{ files: Array<{ filename: string; relPath: string; size: number }> }>(`/api/sessions/${id}/files`, {
			method: "POST",
			body: form,
		});
	},
	skills: (profile: string) => request<{ skills: SkillDetail[] }>(`/api/profiles/${profile}/skills`),
	createSkill: (profile: string, input: { name: string; description: string; body: string }) =>
		request<{ skill: SkillDetail }>(`/api/profiles/${profile}/skills`, { method: "POST", body: JSON.stringify(input) }),
	updateSkill: (profile: string, name: string, input: { description: string; body: string; ifVersion?: string }) =>
		request<{ skill: SkillDetail }>(`/api/profiles/${profile}/skills/${name}`, { method: "PUT", body: JSON.stringify(input) }),
	deleteSkill: (profile: string, name: string) =>
		request<{ ok: true }>(`/api/profiles/${profile}/skills/${name}`, { method: "DELETE" }),
	uploadSkillFiles: (profile: string, name: string, files: File[]) => {
		const form = new FormData();
		for (const f of files) form.append("file", f);
		return request<{ skill: SkillDetail }>(`/api/profiles/${profile}/skills/${name}/files`, { method: "POST", body: form });
	},
	deleteSkillFile: (profile: string, name: string, filename: string) =>
		request<{ ok: true }>(`/api/profiles/${profile}/skills/${name}/files/${encodeURIComponent(filename)}`, { method: "DELETE" }),

	mcpCatalog: () => request<{ servers: McpCatalogEntry[] }>("/api/mcp/catalog"),
	setProfileMcp: (profile: string, servers: string[], ifVersion?: string) =>
		request<McpList>(`/api/mcp/profiles/${profile}`, { method: "PUT", body: JSON.stringify({ servers, ifVersion }) }),
	/** A profile's shared default model; null goes back to the platform default. */
	setProfileModel: (profile: string, model: ModelSpec | null, ifVersion: string) =>
		request<ProfileModelState>(`/api/profiles/${profile}/model`, { method: "PUT", body: JSON.stringify({ model, ifVersion }) }),

	mcpConnections: () => request<{ connections: McpConnection[] }>("/api/mcp/connections"),
	startMcpAuth: (server: string, account: McpAccount) =>
		request<{ authorizationUrl: string | null }>(`/api/mcp/connections/${server}/start?account=${account}`, { method: "POST" }),
	disconnectMcp: (server: string, account: McpAccount) =>
		request<{ ok: true }>(`/api/mcp/connections/${server}?account=${account}`, { method: "DELETE" }),

	loops: () => request<{ loops: LoopListItem[] }>("/api/loops"),
	loop: (id: string) => request<{ loop: LoopDetail }>(`/api/loops/${id}`),
	createLoop: (input: { profile: string; title?: string; goal: string; acceptance: AcceptanceCheck[]; maxIterations?: number }) =>
		request<{ loop: LoopDetail }>("/api/loops", { method: "POST", body: JSON.stringify(input) }),
	loopTrace: (id: string, iteration: string, from: number) => request<TraceChunk>(`/api/loops/${id}/trace/${iteration}?from=${from}`),
	abortLoop: (id: string) => request<{ loop: LoopDetail }>(`/api/loops/${id}/abort`, { method: "POST" }),
	resumeLoop: (id: string) => request<{ loop: LoopDetail }>(`/api/loops/${id}/resume`, { method: "POST" }),
	reviewLoop: (id: string, verdict: { approve: boolean; note?: string }) =>
		request<{ loop: LoopDetail }>(`/api/loops/${id}/review`, { method: "POST", body: JSON.stringify(verdict) }),
	deleteLoop: (id: string) => request<{ ok: true }>(`/api/loops/${id}`, { method: "DELETE" }),

	projects: () => request<{ projects: ProjectSummary[] }>("/api/projects"),
	project: (id: string) => request<ProjectDetail>(`/api/projects/${id}`),
	projectGraph: (id: string) => request<unknown>(`/api/projects/${id}/graph`),
	createProject: (input: { profile: string; title: string }) =>
		request<{ project: ProjectSummary }>("/api/projects", { method: "POST", body: JSON.stringify(input) }),
	updateProject: (id: string, patch: { title?: string; direction?: Record<string, string>; archived?: boolean; ifVersion?: string }) =>
		request<{ project: ProjectSummary }>(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
	saveState: (id: string, body: string, ifVersion?: string) =>
		request<{ state: MemoryEntry }>(`/api/projects/${id}/state`, { method: "PUT", body: JSON.stringify({ body, ifVersion }) }),

	// A research scope's records (ADR-0085, ADR-0086): a project's, or the signed-in person's inbox.
	researchCapabilities: (scope: ResearchScopeRef) => request<ResearchCapabilities>(`${researchApiBase(scope)}/capabilities`),
	researchRecords: (scope: ResearchScopeRef) =>
		request<{ records: StoredRecord[]; latest: StoredRecord[]; revision: string }>(`${researchApiBase(scope)}/records`),
	researchRecord: (scope: ResearchScopeRef, id: string) => request<RecordRead>(`${researchApiBase(scope)}/records/${encodeURIComponent(id)}`),
	researchHistory: (scope: ResearchScopeRef, id: string) =>
		request<{ revisions: StoredRecord[] }>(`${researchApiBase(scope)}/records/${encodeURIComponent(id)}/history`),
	/** Confirms or rejects exactly the revision the request names; a newer one answers 409. */
	researchDecide: (scope: ResearchScopeRef, action: "confirm" | "reject", decision: { id: string; body: { revision: number; reason?: string } }) =>
		request<{ record: ResearchRecord }>(`${researchApiBase(scope)}/records/${encodeURIComponent(decision.id)}/${action}`, { method: "POST", body: JSON.stringify(decision.body) }),
	/** The project's research map, derived on every read (ADR-0087); `focus` and `depth` keep one record's neighbourhood. */
	researchMap: (scope: ResearchScopeRef, query: { focus?: string; depth?: number } = {}) => {
		const q = new URLSearchParams();
		if (query.focus) q.set("focus", query.focus);
		if (query.depth) q.set("depth", String(query.depth));
		const search = q.toString();
		return request<ResearchMapView>(`${researchApiBase(scope)}/map${search ? `?${search}` : ""}`);
	},
	researchEvidence: (scope: ResearchScopeRef) => request<EvidenceSummary>(`${researchApiBase(scope)}/evidence/summary`),
	researchImpacts: (scope: ResearchScopeRef) => request<EvidenceImpacts>(`${researchApiBase(scope)}/evidence/impacts`),
	researchAnalysis: (scope: ResearchScopeRef) => request<AnalysisSummary>(`${researchApiBase(scope)}/analysis/summary`),
	researchAcknowledge: (scope: ResearchScopeRef, ack: { draftId: string; anchorId: string; reviewed: ReviewedRevisions; note?: string }) =>
		request<{ record: ResearchRecord }>(`${researchApiBase(scope)}/evidence/acknowledge`, { method: "POST", body: JSON.stringify(ack) }),
	researchQuality: (scope: ResearchScopeRef) => request<QualitySummary>(`${researchApiBase(scope)}/quality/summary`),

	notebooks: () => request<{ collections: NoteCollection[] }>("/api/notes"),
	notes: (collection: string, filter: { kind?: string; tag?: string; q?: string } = {}) => {
		const params = new URLSearchParams({ collection });
		for (const [key, value] of Object.entries(filter)) if (value) params.set(key, value);
		return request<{ entries: NoteIndexEntry[]; tags: string[] }>(`/api/notes/index?${params}`);
	},
	note: (collection: string, path: string) =>
		request<{ entry: NoteEntry }>(`/api/notes/file?collection=${encodeURIComponent(collection)}&path=${encodeURIComponent(path)}`),
	noteSave: (collection: string, path: string, body: string, ifVersion: string) =>
		request<{ entry: NoteEntry }>(`/api/notes/file?collection=${encodeURIComponent(collection)}&path=${encodeURIComponent(path)}`, {
			method: "PUT",
			body: JSON.stringify({ body, ifVersion }),
		}),
	noteLink: (collection: string, name: string) =>
		request<{ entry: NoteIndexEntry | null }>(`/api/notes/link?collection=${encodeURIComponent(collection)}&name=${encodeURIComponent(name)}`),
	memoryTree: () => request<MemoryTree>("/api/memory"),
	memoryProposals: () => request<{ proposals: MemoryProposal[] }>("/api/memory/proposals"),
	memoryConfirm: (id: string, text?: string) =>
		request<{ ok: true; text: string }>(`/api/memory/proposals/${encodeURIComponent(id)}/confirm`, { method: "POST", body: JSON.stringify(text ? { text } : {}) }),
	memoryReject: (id: string) => request<{ ok: true }>(`/api/memory/proposals/${encodeURIComponent(id)}/reject`, { method: "POST", body: "{}" }),
	memoryFile: (path: string, profile?: string) =>
		request<{ entry: MemoryEntry }>(`/api/memory/file?path=${encodeURIComponent(path)}${profile ? `&profile=${encodeURIComponent(profile)}` : ""}`),
	memoryWrite: (input: { path: string; body: string; title?: string; description?: string; ifVersion?: string }, profile?: string) =>
		request<{ entry: MemoryEntry }>(`/api/memory/file${profile ? `?profile=${encodeURIComponent(profile)}` : ""}`, { method: "PUT", body: JSON.stringify(input) }),
	memoryDelete: (path: string, profile?: string) =>
		request<{ ok: true }>(`/api/memory/file?path=${encodeURIComponent(path)}${profile ? `&profile=${encodeURIComponent(profile)}` : ""}`, { method: "DELETE" }),

	schedules: () => request<ScheduleList>("/api/schedules"),
	schedule: (id: string) => request<ScheduleDetail>(`/api/schedules/${id}`),
	createSchedule: (input: { preset: { profile: string; name: string } } | { profile: string; title: string; repeat: Repeat; text: string; notify: boolean }) =>
		request<{ schedule: ScheduleSummary }>("/api/schedules", { method: "POST", body: JSON.stringify(input) }),
	updateSchedule: (id: string, patch: Partial<Pick<ScheduleSummary, "title" | "text" | "notify" | "paused"> & { repeat: Repeat }>) =>
		request<{ schedule: ScheduleSummary }>(`/api/schedules/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
	deleteSchedule: (id: string) => request<{ ok: true }>(`/api/schedules/${id}`, { method: "DELETE" }),
	fireSchedule: (id: string) => request<{ firing: Firing }>(`/api/schedules/${id}/fire`, { method: "POST" }),
	previewRepeat: (repeat: Repeat) => request<{ timeZone: string; next: number[] }>("/api/schedules/preview", { method: "POST", body: JSON.stringify({ repeat }) }),

	/** Without a status each kind's active states, and archived tickets only when asked for. */
	tasks: (filter: { kind?: TaskKind; status?: TrackedStatus[]; archived?: boolean; projectId?: string; q?: string } = {}) => {
		const params = new URLSearchParams();
		if (filter.kind) params.set("kind", filter.kind);
		if (filter.status?.length) params.set("status", filter.status.join(","));
		if (filter.archived) params.set("archived", "1");
		if (filter.projectId) params.set("projectId", filter.projectId);
		if (filter.q) params.set("q", filter.q);
		const query = params.toString();
		return request<{ tasks: TaskRecord[] }>(`/api/tasks${query ? `?${query}` : ""}`);
	},
	/** A development ticket starts undefined: it takes no status. */
	createTask: (input: { title: string; kind?: TaskKind; description?: string; status?: TaskStatus; priority?: TaskPriority; dueDate?: string | null; projectId?: string | null }) =>
		request<{ task: TaskRecord }>("/api/tasks", { method: "POST", body: JSON.stringify(input) }),
	/** `dependsOn` replaces the task's dependencies; the server refuses cycles and ids it does not offer. A development ticket's status is never sent. */
	updateTask: (id: string, patch: Partial<Pick<TaskRecord, "title" | "description" | "priority" | "dueDate" | "projectId" | "dependsOn" | "kind" | "archived">> & { status?: TaskStatus }) =>
		request<{ task: TaskRecord }>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
	/** The profile whose job asked dev for a development ticket; null for one filed any other way. */
	ticketRequest: (id: string) => request<{ request: TicketRequest | null }>(`/api/tasks/${encodeURIComponent(id)}/request`),
	/** A development ticket's spec as dev prepared it; null until dev did. */
	ticketSpec: (id: string) => request<{ spec: TicketSpec | null }>(`/api/tasks/${encodeURIComponent(id)}/spec`),
	/** A picked ticket's acceptance revisions, oldest first, read-only. */
	acceptanceRevisions: (id: string) => request<AcceptanceRevisionHistory>(`/api/tasks/${encodeURIComponent(id)}/acceptance-revisions`),
	/** The owner's confirmation of exactly the version shown; it makes nothing ready by itself. */
	confirmTicketSpec: (id: string, version: string) =>
		request<{ task: TaskRecord; spec: TicketSpec }>(`/api/tasks/${encodeURIComponent(id)}/confirm-spec`, { method: "POST", body: JSON.stringify({ version }) }),
	/** The ticket's one dev discussion, opened or continued. */
	ticketDiscussion: (id: string) => request<{ session: SessionSummary }>(`/api/tasks/${encodeURIComponent(id)}/discussion`, { method: "POST", body: "{}" }),
	/** The shared ready batch the server deploys together, for review. */
	deploymentBatch: () => request<DeploymentBatch>("/api/tasks/deployment"),
	/** Approves the reviewed batch, pinned at its newest commit. */
	approveDeployment: (sha: string) =>
		request<{ approved: { sha: string; approvedAt: number }; ready: DeploymentBatch["ready"] }>("/api/tasks/deployment/approve", {
			method: "POST",
			body: JSON.stringify({ sha, confirmed: true }),
		}),

	/** Auto mode's pickup controls and this person's task progress; every control answers the fresh status. */
	autoModeStatus: () => request<AutoModeStatus>("/api/auto-mode/status"),
	pauseAutoMode: () => request<AutoModeStatus>("/api/auto-mode/pause", { method: "POST" }),
	/** Turns this person's pickup on, the first time too. */
	resumeAutoMode: () => request<AutoModeStatus>("/api/auto-mode/resume", { method: "POST" }),
	/** The concurrent auto-coding cap both people share, a whole number of at least 1. */
	setAutoModeLimit: (limit: number) => request<AutoModeStatus>("/api/auto-mode/limit", { method: "POST", body: JSON.stringify({ limit }) }),
	/** Recovers the task's interrupted run, a Loop or a one-shot, as the same run; failed work is refused and goes back to dev. */
	resumeAutoTask: (id: string) => request<AutoModeStatus>(`/api/auto-mode/tasks/${encodeURIComponent(id)}/resume`, { method: "POST" }),

	sidebar: () => request<SidebarView>("/api/sidebar"),
	starProfile: (profile: string) => request<SidebarView>(`/api/sidebar/stars/${encodeURIComponent(profile)}`, { method: "PUT" }),
	unstarProfile: (profile: string) => request<SidebarView>(`/api/sidebar/stars/${encodeURIComponent(profile)}`, { method: "DELETE" }),
	createCategory: (name: string) =>
		request<{ category: SidebarCategory; view: SidebarView }>("/api/sidebar/categories", { method: "POST", body: JSON.stringify({ name }) }),
	renameCategory: (id: string, name: string) =>
		request<SidebarView>(`/api/sidebar/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name }) }),
	/** Its profiles go back under Other. */
	removeCategory: (id: string) => request<SidebarView>(`/api/sidebar/categories/${encodeURIComponent(id)}`, { method: "DELETE" }),
	/** A null category files the profile back under Other. */
	placeProfile: (profile: string, categoryId: string | null) =>
		request<SidebarView>(`/api/sidebar/placements/${encodeURIComponent(profile)}`, { method: "PUT", body: JSON.stringify({ categoryId }) }),
	openFold: (key: ExpandedKey) => request<SidebarView>(`/api/sidebar/expanded/${encodeURIComponent(key)}`, { method: "PUT" }),
	closeFold: (key: ExpandedKey) => request<SidebarView>(`/api/sidebar/expanded/${encodeURIComponent(key)}`, { method: "DELETE" }),

	pushKey: () => request<{ publicKey: string }>("/api/push/key"),
	subscribePush: (subscription: PushSubscriptionJSON) => request<{ ok: true }>("/api/push/subscriptions", { method: "POST", body: JSON.stringify(subscription) }),
	unsubscribePush: (endpoint: string) => request<{ ok: true }>("/api/push/subscriptions", { method: "DELETE", body: JSON.stringify({ endpoint }) }),
	testPush: () => request<{ devices: number }>("/api/push/test", { method: "POST" }),

	figmaTokens: () => request<{ tokens: FigmaBridgeToken[] }>("/api/figma/tokens"),
	mintFigmaToken: (label: string) => request<{ token: string; id: string }>("/api/figma/tokens", { method: "POST", body: JSON.stringify({ label }) }),
	revokeFigmaToken: (id: string) => request<{ ok: true }>(`/api/figma/tokens/${id}`, { method: "DELETE" }),
};
